import { api } from "../lib/api.js";

/**
 * Password reset, both halves. Signed-out pages, in the sign-in screen's own
 * frame, since that is where somebody who forgot their password is standing.
 *
 * `#/forgot` asks for the email and always says the same thing afterwards:
 * the server answers identically whether or not the account exists, and so
 * does this page, or it would tell anyone who is a customer.
 *
 * `#/reset?token=...` is the emailed link. A successful reset signs out every
 * browser session (Studio stays paired), then sends them to sign in.
 */
const frame = (inner: string) => `
  <div class="gate"><div class="gate-card">
    <span class="gate-brand"><span class="lamp" aria-hidden="true"></span>Stream-Cut</span>
    ${inner}
  </div></div>`;

export function forgotView(root: HTMLElement) {
  root.innerHTML = frame(`
    <h1>Reset your password</h1>
    <p class="gate-sub">Enter your account's email and we'll send a link to choose a new password.</p>
    <form id="form" novalidate>
      <label>Email <input type="email" id="email" required autocomplete="email"></label>
      <p class="error" id="error" hidden></p>
      <button class="btn primary" type="submit" id="submit">Send the link</button>
    </form>
    <p class="gate-foot"><a href="#/">Back to sign in</a></p>`);
  const form = root.querySelector<HTMLFormElement>("#form")!;
  const email = root.querySelector<HTMLInputElement>("#email")!;
  const error = root.querySelector<HTMLElement>("#error")!;
  const submit = root.querySelector<HTMLButtonElement>("#submit")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!email.value.includes("@")) {
      error.textContent = "Enter the email you signed up with.";
      error.hidden = false;
      return;
    }
    submit.disabled = true;
    try {
      await api("POST", "/v1/auth/forgot", { email: email.value.trim() });
      root.innerHTML = frame(`
        <h1>Check your email</h1>
        <p class="gate-sub">If that address has a Stream-Cut account, a reset link is on its way.
        It works for an hour. Nothing arrived? Check spam, or try again in a few minutes.</p>
        <p class="gate-foot"><a href="#/">Back to sign in</a></p>`);
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : String(err);
      error.hidden = false;
      submit.disabled = false;
    }
  });
}

export function resetView(root: HTMLElement, token: string) {
  root.innerHTML = frame(`
    <h1>Choose a new password</h1>
    <form id="form" novalidate>
      <label>New password <input type="password" id="pw" minlength="10" required autocomplete="new-password" placeholder="At least 10 characters"></label>
      <label>Again <input type="password" id="pw2" minlength="10" required autocomplete="new-password"></label>
      <p class="error" id="error" hidden></p>
      <button class="btn primary" type="submit" id="submit">Save new password</button>
    </form>`);
  const form = root.querySelector<HTMLFormElement>("#form")!;
  const pw = root.querySelector<HTMLInputElement>("#pw")!;
  const pw2 = root.querySelector<HTMLInputElement>("#pw2")!;
  const error = root.querySelector<HTMLElement>("#error")!;
  const submit = root.querySelector<HTMLButtonElement>("#submit")!;
  const fail = (m: string) => {
    error.textContent = m;
    error.hidden = false;
  };
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (pw.value.length < 10) return fail("Use at least 10 characters.");
    if (pw.value !== pw2.value) return fail("The two passwords don't match.");
    submit.disabled = true;
    try {
      await api("POST", "/v1/auth/reset", { token, password: pw.value });
      root.innerHTML = frame(`
        <h1>Password changed</h1>
        <p class="gate-sub">You've been signed out everywhere else. Sign in with your new password --
        in the dashboard, and in the Chrome extension if you use it. Studio stays paired.</p>
        <p class="gate-foot"><a class="btn primary" href="#/">Sign in</a></p>`);
    } catch (err) {
      fail(err instanceof Error ? err.message : String(err));
      submit.disabled = false;
    }
  });
}
